import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMedplum } from '@medplum/react'
import type { DocumentReference } from '@medplum/fhirtypes'
import {
  PROGRESS_NOTE_DOC_TYPE_CODE,
  SCRIBE_DRAFT_DOC_TYPE_CODE,
  SCRIBE_TRANSCRIPT_DOC_TYPE_CODE,
} from '@/model/middleware-types'
import { useClient } from '@/hooks/useClient'
import { logWarning } from '@/utils/errorUtils'

/**
 * Which recovery affordance the call rail should offer, picked from the most
 * advanced artifact the encounter has:
 *
 *   note       → the visit is documented; offer edit/view (PD-1105)
 *   generating → a generation is running right now (PD-1378, possibly in another
 *                tab); show it as a status, offer nothing to click
 *   draft      → generation succeeded but the note was never opened (the client
 *                went away mid-flight); offer to open it
 *   transcript → the transcript persisted but generation failed; offer a retry
 *   none       → nothing happened here yet; offer the join prompt
 */
type EncounterScribeStateValue = 'loading' | 'none' | 'note' | 'generating' | 'draft' | 'transcript'

/** Cadence of the generation re-probe; a draft takes tens of seconds to land. */
const GENERATION_POLL_MS = 5000

export interface EncounterScribeState {
  state: EncounterScribeStateValue
  /** Latest current Progress Note for the encounter, or null. */
  noteId: string | null
  /** Locked (`docStatus === 'final'`) → view-only; otherwise the draft is editable. */
  isLocked: boolean
  draftDocRefId: string | null
  transcriptDocRefId: string | null
  /**
   * Pre-signed URL for the transcript, captured when the search ran so the
   * "View transcript" link can be a plain anchor — opening after an await would
   * lose the user gesture and trip popup blockers. The signature outlives any
   * realistic dwell on this screen; a stale link resolves with a page refresh.
   */
  transcriptUrl: string | null
  /**
   * Re-run the lookup. Call it whenever a note may have been created since the
   * page loaded — otherwise a stale `draft`/`transcript` verdict would re-offer
   * recovery for a visit that is now documented.
   */
  refresh: () => void
}

interface Resolved extends Omit<EncounterScribeState, 'state' | 'refresh'> {
  /** Encounter the values were resolved for; a mismatch with the arg ⇒ still loading. */
  forId: string | undefined
  /** Server-side "a draft generation is in flight for this encounter" flag. */
  generating: boolean
}

const EMPTY: Omit<Resolved, 'forId'> = {
  noteId: null,
  isLocked: false,
  draftDocRefId: null,
  transcriptDocRefId: null,
  transcriptUrl: null,
  generating: false,
}

/**
 * Match on the code alone, ignoring the system — this mirrors the FHIR
 * `type=<code>` token search the Progress Note lookup used before, which also
 * matched regardless of system. The codes are distinctive enough that a
 * cross-system collision is not a real shape.
 */
function findByCode(docRefs: DocumentReference[], code: string): DocumentReference | undefined {
  return docRefs.find((doc) => doc.type?.coding?.some((coding) => coding.code === code))
}

/**
 * A rejoined visit appends to its transcript, which leaves a draft generated
 * before that describing only the first half of the conversation. Medplum
 * stamps both `lastUpdated` server-side, so a transcript newer than the draft
 * means exactly that — drop the draft so the rail offers a regeneration instead
 * of a note that silently omits everything said after the rejoin.
 */
function isSupersededByTranscript(
  draft: DocumentReference | undefined,
  transcript: DocumentReference | undefined,
): boolean {
  const draftAt = Date.parse(draft?.meta?.lastUpdated ?? '')
  const transcriptAt = Date.parse(transcript?.meta?.lastUpdated ?? '')
  return !Number.isNaN(draftAt) && !Number.isNaN(transcriptAt) && transcriptAt > draftAt
}

/**
 * Resolves what already exists for an encounter so the call rail can offer
 * recovery instead of a rejoin. One search covers all three artifacts —
 * `_sort` is newest-first, so `findByCode` picks the most recent of each.
 */
export function useEncounterScribeState(encounterId?: string): EncounterScribeState {
  const medplum = useMedplum()
  const client = useClient()
  const [resolved, setResolved] = useState<Resolved>({ forId: undefined, ...EMPTY })
  const [reloadKey, setReloadKey] = useState(0)
  const refresh = useCallback(() => setReloadKey((key) => key + 1), [])

  useEffect(() => {
    if (!encounterId) return
    let cancelled = false
    // PD-1378 — refresh-driven re-reads must bust the 5s Medplum search cache:
    // a cached pre-draft bundle paired with a fresh `generating: false` would
    // strand the card on a stale Retry. Mount reads keep the cache.
    if (reloadKey > 0) medplum.invalidateSearches('DocumentReference')
    void (async () => {
      try {
        const [docRefs, generation] = await Promise.all([
          medplum.searchResources('DocumentReference', {
            encounter: `Encounter/${encounterId}`,
            status: 'current',
            _sort: '-_lastUpdated',
            _count: '20',
          }),
          // Fail open: the probe is an extra affordance, never a gate — a blip
          // must not mask the artifacts the search did resolve.
          client.scribeGenerationStatus({ encounterId }).catch(() => ({ generating: false })),
        ])
        if (cancelled) return
        const note = findByCode(docRefs, PROGRESS_NOTE_DOC_TYPE_CODE)
        const draft = findByCode(docRefs, SCRIBE_DRAFT_DOC_TYPE_CODE)
        const transcript = findByCode(docRefs, SCRIBE_TRANSCRIPT_DOC_TYPE_CODE)
        const usableDraft = isSupersededByTranscript(draft, transcript) ? undefined : draft
        setResolved({
          forId: encounterId,
          noteId: note?.id ?? null,
          isLocked: note?.docStatus === 'final',
          draftDocRefId: usableDraft?.id ?? null,
          transcriptDocRefId: transcript?.id ?? null,
          transcriptUrl: transcript?.content?.[0]?.attachment?.url ?? null,
          generating: generation.generating,
        })
      } catch (err) {
        // Fail open: on a lookup error fall back to the normal join prompt.
        if (cancelled) return
        logWarning(
          'useEncounterScribeState',
          'Scribe artifact lookup failed — defaulting to join',
          err instanceof Error ? err.message : String(err),
        )
        setResolved({ forId: encounterId, ...EMPTY })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [client, encounterId, medplum, reloadKey])

  // PD-1440 — re-resolve on tab focus: the mount-once snapshot is what let a
  // stale tab offer note creation after another tab locked.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refresh])

  // Derive during render so a changed `encounterId` reads as 'loading' immediately
  // (no frame where a stale value lets the join prompt flash before the notice).
  const state: EncounterScribeStateValue = !encounterId
    ? 'none'
    : resolved.forId !== encounterId
      ? 'loading'
      : resolveState(resolved)

  // PD-1378 — a generation running elsewhere finishes with no event here. Re-probe
  // until the flag clears; that same pass re-runs the document search, so the rail
  // swaps straight from the status card to the finished draft.
  useEffect(() => {
    if (state !== 'generating') return
    const timer = setInterval(refresh, GENERATION_POLL_MS)
    return () => clearInterval(timer)
  }, [state, refresh])

  return useMemo<EncounterScribeState>(() => {
    if (!encounterId || resolved.forId !== encounterId) return { state, refresh, ...EMPTY }
    const { forId: _omitted, ...rest } = resolved
    return { state, refresh, ...rest }
  }, [encounterId, resolved, refresh, state])
}

/**
 * Most advanced artifact wins — a note supersedes an in-flight generation, which
 * supersedes a draft, which supersedes a transcript.
 */
function resolveState(resolved: Resolved): EncounterScribeStateValue {
  if (resolved.noteId) return 'note'
  if (resolved.generating) return 'generating'
  if (resolved.draftDocRefId) return 'draft'
  if (resolved.transcriptDocRefId) return 'transcript'
  return 'none'
}
